import { useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import api from "../../api";

// Paste a draft, get it scored against the rubric and rewritten. The mechanical
// checks (length, the phone fold, dashes, links, bait) come back even when the
// server has no AI key; the scored rewrites need one. Any version, or the draft
// itself, can go straight into the Queue as a drafting item. Every run is kept
// on the server: the latest one reopens when you come back, and earlier ones
// are listed under "Previous runs".
const CHANNELS = [
  { key: "linkedin", label: "LinkedIn" },
  { key: "instagram", label: "Instagram" },
  { key: "newsletter", label: "Newsletter" },
];

const titleOf = (text) => {
  const first = (text || "").trim().split("\n")[0].replace(/["“”]/g, "");
  return first.length > 70 ? `${first.slice(0, 67)}…` : first || "Builder draft";
};

const whenOf = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
};

function Verdicts({ verdicts, labels }) {
  const failed = (verdicts || []).filter((v) => !v.pass);
  const passed = (verdicts || []).filter((v) => v.pass);
  return (
    <div className="mk-b-verdicts">
      {failed.map((v) => (
        <div key={v.id} className={`mk-b-verdict miss ${v.id[0]}`}>
          <span className="mk-check-id">{v.id}</span>
          <b>{labels[v.id] || v.id}.</b> {v.why}
        </div>
      ))}
      {passed.length > 0 && (
        <details>
          <summary>{passed.length} checks passed</summary>
          {passed.map((v) => (
            <div key={v.id} className="mk-b-verdict">
              <span className="mk-check-id">{v.id}</span>
              <b>{labels[v.id] || v.id}.</b> {v.why}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

function ScoreBadge({ score, failedHard }) {
  return (
    <span className={`mk-b-score ${score >= 80 ? "good" : score >= 50 ? "mid" : "low"}`}>
      {score}/100
      {failedHard?.length > 0 && <em> · fails {failedHard.join(", ")}</em>}
    </span>
  );
}

export default function ContentBuilder({ onQueued }) {
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [channel, setChannel] = useState("linkedin");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [res, setRes] = useState(null);
  const [labels, setLabels] = useState({});
  const [queued, setQueued] = useState({});
  const [runs, setRuns] = useState([]);
  const [opening, setOpening] = useState(null);

  const showRun = (run) => {
    setText(run.text || "");
    setNote(run.note || "");
    if (run.channel) setChannel(run.channel);
    setRes(run);
    setQueued({});
    setError(null);
  };

  const openRun = async (id) => {
    setOpening(id);
    try {
      const { data } = await api.get(`/marketing/builder/runs/${id}`);
      showRun(data);
    } catch {
      setError("Couldn't open that run");
    } finally {
      setOpening(null);
    }
  };

  const loadRuns = () => api.get("/marketing/builder/runs")
    .then(({ data }) => { setRuns(data.runs); return data.runs; })
    .catch(() => []);

  useEffect(() => {
    api.get("/marketing/checks")
      .then(({ data }) => setLabels(Object.fromEntries(data.checks.map((c) => [c.id, c.label]))))
      .catch(() => setLabels({}));
    // Come back to where you left off: the latest run, unless one is in progress.
    loadRuns().then((list) => { if (list[0]) openRun(list[0].id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeRun = async (run) => {
    if (!window.confirm(`Remove this run from the list?\n\n"${titleOf(run.preview)}"`)) return;
    try {
      await api.delete(`/marketing/builder/runs/${run.id}`);
      setRuns((rs) => rs.filter((r) => r.id !== run.id));
      if (res?.id === run.id) setRes(null);
    } catch {
      setError("Couldn't remove that run");
    }
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    setRes(null);
    setQueued({});
    try {
      const { data } = await api.post("/marketing/builder", { text, note: note || null, channel });
      setRes(data);
      loadRuns();
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't run the builder");
    } finally {
      setBusy(false);
    }
  };

  const addToQueue = async (key, body) => {
    setQueued((q) => ({ ...q, [key]: "adding" }));
    try {
      await api.post("/marketing/items", {
        title: titleOf(body), body, channels: [channel], state: "drafting", author: "ward",
      });
      setQueued((q) => ({ ...q, [key]: "added" }));
      onQueued?.();
    } catch {
      setQueued((q) => ({ ...q, [key]: "failed" }));
    }
  };

  const queueButton = (key, body) => (
    <button type="button" className="mk-btn-quiet" disabled={queued[key] === "adding" || queued[key] === "added"}
      onClick={() => addToQueue(key, body)} data-testid={`mk-b-queue-${key}`}>
      {queued[key] === "added" ? "In the Queue ✓" : queued[key] === "failed" ? "Failed, try again" : "Add to Queue"}
    </button>
  );

  const mech = res?.mechanical;
  const ai = res?.ai;

  return (
    <div className="mk-builder" data-testid="mk-builder">
      <p className="mk-b-intro">
        Paste a post the way you'd say it. The builder scores it against the rubric and writes
        three versions: closest to you, highest score, and bolder. Every version tells you why
        it scored what it did, and anything it added that isn't yours is marked for you to confirm.
      </p>

      <label className="mk-label" htmlFor="mk-b-text">Your draft</label>
      <textarea id="mk-b-text" className="mk-textarea" rows={12} value={text}
        onChange={(e) => setText(e.target.value)} data-testid="mk-b-text"
        placeholder="Paste the post here…" />
      <div className="mk-b-count">{text.length} characters</div>

      <label className="mk-label" htmlFor="mk-b-note">What should it do? (optional)</label>
      <input id="mk-b-note" className="mk-input" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. I want to thank Desmond and honor him for 19 years of trust" />

      <div className="mk-b-row">
        <select className="mk-select" value={channel} onChange={(e) => setChannel(e.target.value)}>
          {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button type="button" className="mk-btn" disabled={busy || !text.trim()} onClick={generate}
          data-testid="mk-b-generate">
          {busy ? "Working… about a minute" : "Score and rewrite"}
        </button>
        {text.trim() && queueButton("draft", text)}
      </div>

      {error && <div className="mk-b-error">{error}</div>}

      {mech && (
        <div className="mk-b-card">
          <div className="mk-label">Quick checks (instant, no AI)</div>
          <div className="mk-b-fold">
            <span className="mk-b-dim">What a phone shows before "see more":</span>
            <div>{mech.fold}{mech.chars > mech.fold.length ? "…" : ""}</div>
          </div>
          <div className="mk-b-dim">{mech.chars} characters · {mech.words} words</div>
          {mech.flags.length === 0
            ? <div className="mk-b-verdict">Nothing mechanical to fix.</div>
            : mech.flags.map((f, i) => (
              <div key={i} className="mk-b-verdict miss">
                <span className="mk-check-id">{f.check}</span>{f.message}
              </div>
            ))}
        </div>
      )}

      {res?.ai_status === "not_connected" && (
        <div className="mk-b-card mk-b-dim">
          The AI half isn't connected yet, so only the quick checks ran. Once Marco adds the
          AI key to the server, this button also scores the post and writes the three versions.
        </div>
      )}
      {res?.ai_status === "error" && <div className="mk-b-error">{res.ai_error}</div>}

      {ai && (
        <>
          <div className="mk-b-card">
            <div className="mk-b-head">
              <span className="mk-label">Your draft</span>
              <ScoreBadge score={ai.original.score} failedHard={ai.original.failed_hard} />
            </div>
            <p>{ai.summary}</p>
            <Verdicts verdicts={ai.original.verdicts} labels={labels} />
          </div>

          {ai.versions.map((v, i) => (
            <div key={i} className="mk-b-card" data-testid={`mk-b-version-${i}`}>
              <div className="mk-b-head">
                <span className="mk-label">{v.label}</span>
                <ScoreBadge score={v.score} failedHard={v.failed_hard} />
              </div>
              <div className="mk-b-dim">{v.approach}</div>
              <div className="mk-b-text">{v.text}</div>
              <div className="mk-b-dim">{v.mechanical.chars} characters</div>
              {v.ward_must_confirm?.length > 0 && (
                <div className="mk-b-confirm">
                  <b>Not your words yet. Confirm or change before posting:</b>
                  <ul>{v.ward_must_confirm.map((c, j) => <li key={j}>{c}</li>)}</ul>
                </div>
              )}
              {v.changes?.length > 0 && (
                <details>
                  <summary>What changed and why</summary>
                  <ul>{v.changes.map((c, j) => <li key={j}>{c}</li>)}</ul>
                </details>
              )}
              <details>
                <summary>Why it scored {v.score}</summary>
                <Verdicts verdicts={v.verdicts} labels={labels} />
              </details>
              <div className="mk-b-row">
                <button type="button" className="mk-btn-quiet"
                  onClick={() => navigator.clipboard?.writeText(v.text)}>Copy</button>
                <button type="button" className="mk-btn-quiet" onClick={() => setText(v.text)}>
                  Edit this one
                </button>
                {queueButton(`v${i}`, v.text)}
              </div>
            </div>
          ))}

          {ai.questions_for_ward?.length > 0 && (
            <div className="mk-b-card">
              <div className="mk-label">Answer these to raise the score</div>
              <ul>{ai.questions_for_ward.map((q, i) => <li key={i}>{q}</li>)}</ul>
            </div>
          )}
        </>
      )}

      {runs.length > 0 && (
        <div className="mk-b-history" data-testid="mk-b-history">
          <div className="mk-label">Previous runs</div>
          {runs.map((r) => (
            <div key={r.id} className={`mk-b-run${res?.id === r.id ? " on" : ""}`}>
              <button type="button" className="mk-b-run-open" onClick={() => openRun(r.id)}
                disabled={opening === r.id} data-testid={`mk-b-run-${r.id}`}>
                <span className="mk-b-run-title">{titleOf(r.preview)}</span>
                <span className="mk-b-dim">
                  {whenOf(r.at)} · {CHANNELS.find((c) => c.key === r.channel)?.label || r.channel}
                  {r.best_score != null
                    ? ` · ${r.original_score ?? "?"} → best ${r.best_score}/100`
                    : " · quick checks only"}
                  {res?.id === r.id ? " · showing" : ""}
                </span>
              </button>
              <button type="button" className="mk-b-run-del" title="Remove this run"
                aria-label="Remove this run" onClick={() => removeRun(r)}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
